// src/components/admin/UserManagement.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaEdit, FaTrash, FaCheck, FaTimes, FaUserShield } from 'react-icons/fa';
import {
    fetchUsers,
    updateUserRole,
    toggleUserStatus,
    deleteUser
} from '../../features/admin/adminSlice';

const UserManagement = () => {
    const dispatch = useDispatch();
    const { users, isLoading, error } = useSelector(state => state.admin);
    const { user: currentUser } = useSelector(state => state.auth);

    const [selectedUser, setSelectedUser] = useState(null);
    const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
    const [newRole, setNewRole] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredUsers, setFilteredUsers] = useState([]);

    useEffect(() => {
        dispatch(fetchUsers());
    }, [dispatch]);

    useEffect(() => {
        if (users) {
            setFilteredUsers(
                users.filter(user =>
                    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
        }
    }, [users, searchTerm]);

    const handleToggleStatus = (userId, currentStatus) => {
        dispatch(toggleUserStatus({
            userId,
            isActive: !currentStatus
        }));
    };

    const openRoleModal = (user) => {
        setSelectedUser(user);
        setNewRole(user.role);
        setIsRoleModalOpen(true);
    };

    const handleRoleChange = () => {
        dispatch(updateUserRole({
            userId: selectedUser.id,
            role: newRole
        }));
        setIsRoleModalOpen(false);
    };

    const openDeleteModal = (user) => {
        setSelectedUser(user);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteUser = () => {
        dispatch(deleteUser(selectedUser.id));
        setIsDeleteModalOpen(false);
    };

    // Role badge component
    // Replace your RoleBadge component with this improved version
    // Replace your RoleBadge component with this version using inline styles:

    const RoleBadge = ({ role }) => {
        const safeRole = role || 'user';

        // Define styles based on role
        let badgeStyle = {
            display: 'inline-block',
            padding: '0.25em 0.6em',
            fontSize: '0.75em',
            fontWeight: '700',
            lineHeight: '1',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            verticalAlign: 'baseline',
            borderRadius: '0.375rem',
            color: '#fff',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        };

        switch (safeRole.toLowerCase()) {
            case 'super_admin':
                badgeStyle.backgroundColor = '#dc3545'; // Red
                break;
            case 'admin':
                badgeStyle.backgroundColor = '#ffc107'; // Orange/Yellow
                badgeStyle.color = '#212529'; // Dark text for better contrast
                break;
            case 'user':
            default:
                badgeStyle.backgroundColor = '#007bff'; // Blue
        }

        return (
            <span style={badgeStyle} title={`Role: ${safeRole}`}>
                {safeRole.replace('_', ' ')}
            </span>
        );
    };

    if (isLoading) {
        return <div className="loading">Loading users...</div>;
    }

    return (
        <div className="admin-container">
            <h1>User Management</h1>

            <div className="admin-toolbar">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="user-table-container">
                <table className="user-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Verified</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map(user => (
                            <tr key={user.id}>
                                <td>{user.first_name} {user.last_name}</td>
                                <td>{user.email}</td>
                                <td>
                                    <RoleBadge role={user.role} />
                                </td>
                                <td>
                                    {user.is_verified ? (
                                        <FaCheck className="icon-success" />
                                    ) : (
                                        <FaTimes className="icon-danger" />
                                    )}
                                </td>
                                <td>
                                    <span className={`status ${user.is_active ? 'active' : 'inactive'}`}>
                                        {user.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td className="actions">
                                    {user.id !== currentUser.id && (
                                        <>
                                            <button
                                                className="btn-icon"
                                                onClick={() => handleToggleStatus(user.id, user.is_active)}
                                                title={user.is_active ? 'Deactivate User' : 'Activate User'}
                                            >
                                                {user.is_active ?
                                                    <FaTimes className="icon-danger" /> :
                                                    <FaCheck className="icon-success" />
                                                }
                                            </button>

                                            {currentUser.role === 'super_admin' && (
                                                <button
                                                    className="btn-icon"
                                                    onClick={() => openRoleModal(user)}
                                                    title="Change Role"
                                                >
                                                    <FaUserShield />
                                                </button>
                                            )}

                                            {currentUser.role === 'super_admin' && (
                                                <button
                                                    className="btn-icon btn-danger"
                                                    onClick={() => openDeleteModal(user)}
                                                    title="Delete User"
                                                >
                                                    <FaTrash />
                                                </button>
                                            )}
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Role Change Modal */}
            {isRoleModalOpen && selectedUser && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h2>Change User Role</h2>
                        <p>
                            Change role for {selectedUser.first_name} {selectedUser.last_name}
                        </p>

                        <div className="form-group">
                            <label htmlFor="role">Select Role</label>
                            <select
                                id="role"
                                value={newRole}
                                onChange={(e) => setNewRole(e.target.value)}
                            >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                                <option value="super_admin">Super Admin</option>
                            </select>
                        </div>

                        <div className="modal-actions">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setIsRoleModalOpen(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleRoleChange}
                            >
                                Update Role
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && selectedUser && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h2>Delete User</h2>
                        <p>
                            Are you sure you want to delete the account for {selectedUser.first_name} {selectedUser.last_name}?
                        </p>
                        <p className="warning-text">
                            This action cannot be undone.
                        </p>

                        <div className="modal-actions">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setIsDeleteModalOpen(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-danger"
                                onClick={handleDeleteUser}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <style jsx>
                {`
                /* Add these styles to your CSS file (e.g., UserManagement.css or your main CSS file) */

.badge {
    display: inline-block;
    padding: 0.25em 0.6em;
    font-size: 0.75em;
    font-weight: 700;
    line-height: 1;
    text-align: center;
    white-space: nowrap;
    vertical-align: baseline;
    border-radius: 0.375rem;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.badge-primary {
    background-color: #007bff;
    color: #fff;
}

.badge-warning {
    background-color: #ffc107;
    color: #212529;
}

.badge-danger {
    background-color: #dc3545;
    color: #fff;
}

/* Optional: Add hover effects */
.badge:hover {
    opacity: 0.8;
    transform: scale(1.05);
    transition: all 0.2s ease;
}

/* Optional: Make badges slightly larger for better visibility */
.user-table .badge {
    font-size: 0.8em;
    padding: 0.3em 0.8em;
}
                `}
            </style>
        </div>
    );
};

export default UserManagement;
